import Button from 'admin/components/atoms/Buttons';
import FA from 'admin/components/atoms/FontAwesome';
import Input from 'admin/components/atoms/Input';
import Link from 'admin/components/atoms/Link';
import NumberInput from 'admin/components/atoms/NumberInput';
import Table from 'admin/components/atoms/Table';
import BreadCrumb from 'admin/components/molecules/BreadCrumb';
import {
  isPresetGroupNameEn,
  PRESET_GROUPS,
  PresetGroup,
} from 'admin/constants/userTagGroupLabels';
import React, { useCallback, useEffect, useState } from 'react';
import Modal from 'react-modal';
import { Alert } from 'reactstrap';
import styled, { css } from 'styled-components';
import {
  createUserTagGroup,
  deleteUserTagGroup,
  getUserTagGroups,
  updateUserTagGroup,
  UserTagGroup,
} from '~/javascripts/admin/actions/user-tag-group';
import { useAppSelector } from '~/javascripts/admin/hooks/useApp';
import { selectUserPrivateMoney } from '~/javascripts/admin/reducers/user-private-money';

const CRUMBS = [
  { value: 'ホーム', href: '/', isCurrentPage: false },
  { value: '各種設定', href: '', isCurrentPage: true },
  { value: '店舗ラベル管理', href: '', isCurrentPage: true },
];

type Header = {
  label: string;
  size: number;
};

const HEADERS: Header[] = [
  { label: 'グループ名', size: 5 },
  { label: '選択上限', size: 2 },
  { label: '状態', size: 2 },
  { label: '操作', size: 3 },
];

const modalStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-30%',
    transform: 'translate(-50%, -50%)',
    minWidth: '400px',
    padding: '24px',
  },
  overlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
};

const AddButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  margin-bottom: 16px;
`;

const PageHeaderRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 12px;
  margin-bottom: 24px;
`;

const PageHeader = styled.h1`
  font-size: ${({ theme }) => theme.typography.heading3.bold.fontSize};
  font-weight: ${({ theme }) => theme.typography.heading3.bold.fontWeight};
  line-height: ${({ theme }) => theme.typography.heading3.bold.lineHeight};
  color: ${({ theme }) => theme.colors.gray['400']};
  margin-bottom: 0;
`;

// サービス権限ユーザー向け: 「ラベルグループ一覧」の右側に表示する補足情報。
// 現在どの発行体のグループを表示しているかを明示する。
const IssuerInfo = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.gray['300']};
`;

const ModalTitle = styled.h2`
  font-size: ${({ theme }) => theme.typography.heading3.bold.fontSize};
  font-weight: ${({ theme }) => theme.typography.heading3.bold.fontWeight};
  color: ${({ theme }) => theme.colors.gray['400']};
  margin-bottom: 16px;
  text-align: center;
`;

const FormRow = styled.div`
  margin-bottom: 16px;
`;

const FormLabel = styled.label`
  display: block;
  font-size: 14px;
  margin-bottom: 4px;
  color: ${({ theme }) => theme.colors.gray['400']};
`;

const FieldHint = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.gray['300']};
  margin-top: 4px;
`;

const ReadOnlyValue = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.gray['400']};
`;

const ModalButtonGroup = styled.div`
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-top: 16px;
`;

const ActionGroup = styled.div`
  display: flex;
  gap: 8px;
`;

// 操作ボタン: 白背景・緑枠（outline）で統一。テーブル内テキストと同じ 14px。
const ActionButton = styled(Button)`
  min-width: 64px;
  padding: 4px 12px;
  font-size: 14px;
`;

// 削除は破壊的操作のため、白背景・赤枠・赤文字にする。
const DeleteButton = styled(ActionButton)`
  border-color: #d75f48;
  color: #d75f48;
  &:hover {
    background: #fdf3f1;
  }
`;

// グループ名から詳細（ラベル一覧）へ遷移するリンク。
const GroupNameLink = styled(Link)`
  &:hover {
    text-decoration: underline;
  }
`;

// テーブル内のテキストサイズを統一する
const tableTextStyle = css`
  font-size: 14px;
`;

const LoadingText = styled.div`
  color: ${({ theme }) => theme.colors.gray['300']};
  padding: 16px 0;
`;

const WarningMessage = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  color: ${({ theme }) => theme.semanticColors.error};
  margin-bottom: 16px;
`;

const ErrorMessage = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${({ theme }) => theme.semanticColors.error};
  font-size: 14px;
  margin-top: 8px;
`;

const Badge = styled.span<{ $registered: boolean }>`
  ${({ theme: { colors, borderRadius }, $registered }) => css`
    display: inline-block;
    padding: 2px 10px;
    border-radius: ${borderRadius.full};
    font-size: 14px;
    color: white;
    background-color: ${$registered ? colors.green['400'] : colors.gray['300']};
  `}
`;

// グループ名の隣に出す「3階層（サブグループあり）」の控えめなタグ。
const SubgroupTag = styled.span`
  ${({ theme: { colors, borderRadius } }) => css`
    display: inline-block;
    margin-left: 8px;
    padding: 1px 8px;
    border-radius: ${borderRadius.full};
    font-size: 12px;
    color: ${colors.gray['400']};
    background-color: ${colors.gray['100']};
    border: 1px solid ${colors.gray['200']};
    vertical-align: middle;
  `}
`;

// 未登録プリセットは UserTagGroup と区別するためのビュー型
type PresetRow = PresetGroup & { kind: 'preset' };
type GroupRow = (UserTagGroup & { kind: 'registered' }) | PresetRow;

const formatMaxSelections = (max: number): string => (max === 0 ? '無制限' : String(max));

const SettingsShopLabel = () => {
  const { organization: privateMoneyOrg } = useAppSelector(selectUserPrivateMoney);
  const user = useAppSelector((state) => state.user);
  // サービス権限のときのみ発行体名を表示する（ヘッダのマネー切替で発行体が変わるため）
  const isServiceUser = typeof user?.isServiceUser === 'function' && user.isServiceUser();

  const [groups, setGroups] = useState<UserTagGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // 追加モーダル
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [addName, setAddName] = useState('');
  const [addMaxSelections, setAddMaxSelections] = useState('1');
  const [addError, setAddError] = useState<string | null>(null);

  // 編集モーダル（max_selections は作成時のみ設定可・編集不可）
  const [editingGroup, setEditingGroup] = useState<UserTagGroup | null>(null);
  const [editName, setEditName] = useState('');
  const [editError, setEditError] = useState<string | null>(null);

  // 削除モーダル
  const [deletingGroup, setDeletingGroup] = useState<UserTagGroup | null>(null);

  const [showSuccess, setShowSuccess] = useState<string | null>(null);

  const flashSuccess = (message: string) => {
    setShowSuccess(message);
    setTimeout(() => setShowSuccess(null), 3000);
  };

  const fetchGroups = useCallback(async () => {
    if (!privateMoneyOrg?.code) return;

    setLoading(true);
    try {
      // マスタ一覧では無効化（is_active=false）済みのグループも含めて表示する
      const response = await getUserTagGroups(privateMoneyOrg.code, 'all');
      setGroups(response.rows);
    } catch (error) {
      console.error('Failed to fetch user tag groups:', error);
      setGroups([]);
    } finally {
      setLoading(false);
    }
  }, [privateMoneyOrg?.code]);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  // 登録済みグループ + 未登録プリセットをマージ
  const registeredNameEns = new Set(groups.map((g) => g.name_en));
  const unregisteredPresets: PresetRow[] = PRESET_GROUPS.filter(
    (preset) => !registeredNameEns.has(preset.name_en)
  ).map((preset) => ({ ...preset, kind: 'preset' as const }));
  const rows: GroupRow[] = [
    ...groups.map((g) => ({ ...g, kind: 'registered' as const })),
    ...unregisteredPresets,
  ];

  const parseMaxSelections = (raw: string): number => {
    const n = Number(raw);
    if (!Number.isFinite(n) || n < 0) return 0;
    return Math.floor(n);
  };

  const isDuplicateName = (nameJa: string, excludeId?: string): boolean =>
    groups.some((g) => g.id !== excludeId && g.name_ja === nameJa);

  // --- 追加 ---
  const openAdd = () => {
    setAddName('');
    setAddMaxSelections('1');
    setAddError(null);
    setIsAddOpen(true);
  };
  const closeAdd = () => setIsAddOpen(false);

  const handleAdd = async () => {
    const name = addName.trim();
    if (!name || !privateMoneyOrg?.code) return;
    if (isDuplicateName(name)) {
      setAddError('同名のグループが既に登録されています');
      return;
    }

    setSaving(true);
    try {
      await createUserTagGroup({
        organization_code: privateMoneyOrg.code,
        name_en: name,
        name_ja: name,
        max_selections: parseMaxSelections(addMaxSelections),
      });
      closeAdd();
      await fetchGroups();
      flashSuccess('グループを追加しました');
    } catch (error) {
      console.error('Failed to create user tag group:', error);
      setAddError('グループの追加に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  // --- プリセット有効化 ---
  const handleActivatePreset = async (preset: PresetRow) => {
    if (!privateMoneyOrg?.code) return;
    setSaving(true);
    try {
      await createUserTagGroup({
        organization_code: privateMoneyOrg.code,
        name_en: preset.name_en,
        name_ja: preset.name_ja,
        max_selections: 1,
      });
      await fetchGroups();
      flashSuccess('プリセットを有効化しました');
    } catch (error) {
      console.error('Failed to activate preset group:', error);
    } finally {
      setSaving(false);
    }
  };

  // --- 編集 ---
  const openEdit = (group: UserTagGroup) => {
    setEditingGroup(group);
    setEditName(group.name_ja || group.name_en);
    setEditError(null);
  };
  const closeEdit = () => setEditingGroup(null);

  const handleEditSave = async () => {
    if (!editingGroup) return;
    const name = editName.trim();
    if (!name) {
      setEditError('グループ名を入力してください');
      return;
    }
    if (isDuplicateName(name, editingGroup.id)) {
      setEditError('同名のグループが既に登録されています');
      return;
    }

    setSaving(true);
    try {
      // プリセット由来グループは name_en を照合キーとして保持するため変更しない。
      // max_selections は作成時のみ設定可・編集では変更しない（運用途中の変更による混乱を防ぐ）。
      const isPreset = isPresetGroupNameEn(editingGroup.name_en);
      await updateUserTagGroup(editingGroup.id, {
        name_ja: name,
        ...(isPreset ? {} : { name_en: name }),
      });
      closeEdit();
      await fetchGroups();
      flashSuccess('グループを更新しました');
    } catch (error) {
      console.error('Failed to update user tag group:', error);
      setEditError('グループの更新に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  // --- 削除 ---
  const openDelete = (group: UserTagGroup) => setDeletingGroup(group);
  const closeDelete = () => setDeletingGroup(null);

  const handleDelete = async () => {
    if (!deletingGroup) return;
    setSaving(true);
    try {
      await deleteUserTagGroup(deletingGroup.id);
      closeDelete();
      await fetchGroups();
      flashSuccess('グループを削除しました');
    } catch (error) {
      console.error('Failed to delete user tag group:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <BreadCrumb crumbs={CRUMBS} />
      <Alert color="success" isOpen={showSuccess !== null}>
        {showSuccess}
      </Alert>
      <AddButtonWrapper>
        <Button onClick={openAdd}>グループを追加</Button>
      </AddButtonWrapper>
      <PageHeaderRow>
        <PageHeader>ラベルグループ一覧</PageHeader>
        {isServiceUser && privateMoneyOrg?.name && (
          <IssuerInfo>
            発行体: {privateMoneyOrg.name} {privateMoneyOrg.code}
          </IssuerInfo>
        )}
      </PageHeaderRow>
      {loading ? (
        <LoadingText>読み込み中...</LoadingText>
      ) : (
        <Table styles={tableTextStyle}>
          <Table.Header>
            <Table.Row>
              {HEADERS.map((h) => (
                <Table.Cell key={h.label} size={h.size}>
                  {h.label}
                </Table.Cell>
              ))}
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {rows.map((row) =>
              row.kind === 'registered' ? (
                <Table.Row key={`group-${row.id}`}>
                  <Table.Cell>
                    {row.is_active ? (
                      <GroupNameLink to={`/settings/shop-label/${row.id}`}>
                        {row.name_ja || row.name_en}
                      </GroupNameLink>
                    ) : (
                      <span>{row.name_ja || row.name_en}</span>
                    )}
                    {row.has_subgroups && <SubgroupTag>サブグループあり</SubgroupTag>}
                  </Table.Cell>
                  <Table.Cell>{formatMaxSelections(row.max_selections)}</Table.Cell>
                  <Table.Cell>
                    {row.is_active ? (
                      <Badge $registered>登録済み</Badge>
                    ) : (
                      <Badge $registered={false}>無効（削除済み）</Badge>
                    )}
                  </Table.Cell>
                  <Table.Cell>
                    {row.is_active && (
                      <ActionGroup>
                        <ActionButton variant="outline" onClick={() => openEdit(row)}>
                          編集
                        </ActionButton>
                        <DeleteButton variant="outline" onClick={() => openDelete(row)}>
                          削除
                        </DeleteButton>
                      </ActionGroup>
                    )}
                  </Table.Cell>
                </Table.Row>
              ) : (
                <Table.Row key={`preset-${row.name_en}`}>
                  <Table.Cell>{row.name_ja}</Table.Cell>
                  <Table.Cell>—</Table.Cell>
                  <Table.Cell>
                    <Badge $registered={false}>未登録（プリセット）</Badge>
                  </Table.Cell>
                  <Table.Cell>
                    <ActionButton
                      variant="outline"
                      onClick={() => handleActivatePreset(row)}
                      disabled={saving}
                    >
                      有効化
                    </ActionButton>
                  </Table.Cell>
                </Table.Row>
              )
            )}
          </Table.Body>
        </Table>
      )}

      {/* 追加モーダル */}
      <Modal
        isOpen={isAddOpen}
        onRequestClose={closeAdd}
        shouldCloseOnOverlayClick
        closeTimeoutMS={200}
        ariaHideApp={false}
        style={modalStyles}
      >
        <ModalTitle>グループを追加</ModalTitle>
        <FormRow>
          <FormLabel>グループ名</FormLabel>
          <Input
            fullWidth
            syncValue={addName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setAddName(e.target.value);
              setAddError(null);
            }}
          />
        </FormRow>
        <FormRow>
          <FormLabel>選択上限</FormLabel>
          <NumberInput
            min={0}
            value={addMaxSelections}
            setValue={(v) => setAddMaxSelections(v)}
          />
          <FieldHint>1店舗あたりに付与できるラベル数の上限。0 を指定すると無制限。</FieldHint>
        </FormRow>
        {addError && (
          <ErrorMessage>
            <span>{addError}</span>
          </ErrorMessage>
        )}
        <ModalButtonGroup>
          <Button variant="neutral" onClick={closeAdd} disabled={saving}>
            キャンセル
          </Button>
          <Button onClick={handleAdd} disabled={saving || !addName.trim()}>
            追加
          </Button>
        </ModalButtonGroup>
      </Modal>

      {/* 編集モーダル */}
      <Modal
        isOpen={editingGroup !== null}
        onRequestClose={closeEdit}
        shouldCloseOnOverlayClick
        closeTimeoutMS={200}
        ariaHideApp={false}
        style={modalStyles}
      >
        <ModalTitle>グループを編集</ModalTitle>
        <FormRow>
          <FormLabel>グループ名</FormLabel>
          <Input
            fullWidth
            syncValue={editName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setEditName(e.target.value);
              setEditError(null);
            }}
          />
        </FormRow>
        <FormRow>
          <FormLabel>選択上限</FormLabel>
          <ReadOnlyValue>
            {editingGroup ? formatMaxSelections(editingGroup.max_selections) : ''}
          </ReadOnlyValue>
          <FieldHint>選択上限は作成時のみ設定でき、後から変更できません。</FieldHint>
        </FormRow>
        {editError && (
          <ErrorMessage>
            <span>{editError}</span>
          </ErrorMessage>
        )}
        <ModalButtonGroup>
          <Button variant="neutral" onClick={closeEdit} disabled={saving}>
            キャンセル
          </Button>
          <Button onClick={handleEditSave} disabled={saving || !editName.trim()}>
            変更する
          </Button>
        </ModalButtonGroup>
      </Modal>

      {/* 削除確認モーダル */}
      <Modal
        isOpen={deletingGroup !== null}
        onRequestClose={closeDelete}
        shouldCloseOnOverlayClick
        closeTimeoutMS={200}
        ariaHideApp={false}
        style={modalStyles}
      >
        <ModalTitle>グループを削除します</ModalTitle>
        <WarningMessage>
          <FA icon="exclamation-triangle" />
          <span>
            削除すると、このグループと配下のラベルは無効になります。すでに店舗に紐づいている
            ラベルにも影響があります。元に戻せません。
          </span>
        </WarningMessage>
        <ModalButtonGroup>
          <Button variant="neutral" onClick={closeDelete} disabled={saving}>
            キャンセル
          </Button>
          <Button variant="danger" onClick={handleDelete} disabled={saving}>
            削除する
          </Button>
        </ModalButtonGroup>
      </Modal>
    </>
  );
};

export default SettingsShopLabel;
